import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  forgotPasswordSchema,
  type ForgotPasswordFormData,
} from '@/schemas/auth'
import { useAuthStore } from '@/stores/auth-store'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useState, useEffect, ComponentProps } from 'react'
import { ArrowLeft, Loader2, Brain, MailCheck } from 'lucide-react'
import { BackgroundWrapper } from '@/components/ui/background-wrapper'

function ForgotPasswordForm({ className, ...props }: ComponentProps<'div'>) {
  const { forgotPassword, isLoading, error, validationErrors, resetAuthState } =
    useAuthStore()
  const [isSuccess, setIsSuccess] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')

  // Reset auth state when component mounts (handles back navigation from OAuth)
  useEffect(() => {
    resetAuthState()
  }, [resetAuthState])

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await forgotPassword(data.email)
      setSubmittedEmail(data.email)
      setIsSuccess(true)
    } catch (error) {
      // Handle specific error codes from backend
      if (error instanceof Error) {
        const errorMessage = error.message

        // Check if this is an OAuth user error
        if (errorMessage.includes('Google sign-in')) {
          setError('email', {
            type: 'server',
            message:
              'This account was created using Google sign-in. Please use the "Sign in with Google" option instead.',
          })
        } else {
          const { validationErrors: currentErrors } = useAuthStore.getState()
          if (currentErrors) {
            Object.entries(currentErrors).forEach(([field, messages]) => {
              setError(field as keyof ForgotPasswordFormData, {
                type: 'server',
                message: messages[0],
              })
            })
          }
        }
      }
    }
  }

  if (isSuccess) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <Card className="overflow-hidden p-0 bg-white dark:bg-white text-black dark:text-black border-gray-200 dark:border-gray-200 shadow-sm dark:shadow-sm">
          <CardContent className="p-6 md:p-8">
            <div>
              <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white border border-gray-200">
                      <Brain className="h-5 w-5 text-black" />
                    </div>
                    <span className="text-lg font-semibold">ThoughtFabric</span>
                  </div>
                  <div className="mb-4">
                    <MailCheck className="h-12 w-12 text-green-600 mx-auto" />
                  </div>
                  <h1 className="text-2xl font-bold">Check your email</h1>
                  <p className="text-xs text-muted-foreground text-balance">
                    If an account with that email exists, you will receive a
                    password reset email at <strong>{submittedEmail}</strong>
                  </p>
                </div>

                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Didn't receive the email? Check your spam folder or try
                    again.
                  </p>

                  <Button
                    onClick={() => setIsSuccess(false)}
                    variant="outline"
                    className="w-full bg-white dark:bg-white border-gray-200 dark:border-gray-200 text-black dark:text-black hover:bg-gray-50 dark:hover:bg-gray-50"
                  >
                    Try another email
                  </Button>

                  <div className="text-center text-sm">
                    Remember your password?{' '}
                    <Link
                      to="/auth/login"
                      className="underline underline-offset-4 hover:text-black"
                    >
                      Sign in
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden p-0 bg-white dark:bg-white text-black dark:text-black border-gray-200 dark:border-gray-200 shadow-sm dark:shadow-sm">
        <CardContent className="p-6 md:p-8">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white border border-gray-200">
                    <Brain className="h-5 w-5 text-black" />
                  </div>
                  <span className="text-lg font-semibold">ThoughtFabric</span>
                </div>
                <h1 className="text-2xl font-bold">Forgot your password?</h1>
                <p className="text-xs text-muted-foreground text-balance">
                  Enter your email address and we'll send you a link to reset
                  your password
                </p>
              </div>

              {error && (
                <div className="bg-destructive/15 border border-destructive/20 text-destructive text-xs px-4 py-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="grid gap-3">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  {...register('email')}
                  className={cn(
                    'bg-white dark:bg-white border-gray-200 dark:border-gray-200 text-black dark:text-black placeholder:text-gray-500 dark:placeholder:text-gray-500',
                    errors.email || validationErrors?.email
                      ? 'border-destructive'
                      : ''
                  )}
                />
                {errors.email && (
                  <p className="text-xs text-destructive">
                    {errors.email.message}
                  </p>
                )}
                {validationErrors?.email && (
                  <p className="text-xs text-destructive">
                    {validationErrors.email[0]}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-black hover:bg-black/90 text-white"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending reset link...
                  </>
                ) : (
                  'Send reset link'
                )}
              </Button>

              <div className="text-center text-sm">
                <Link
                  to="/auth/login"
                  className="inline-flex items-center gap-2 text-muted-foreground hover:text-black"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </Link>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
      <div className="text-center text-xs text-balance text-gray-400">
        By continuing, you agree to our{' '}
        <a
          href="https://thoughtfabric.ai/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-white transition-colors"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://thoughtfabric.ai/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-white transition-colors"
        >
          Privacy Policy
        </a>
        .
      </div>
    </div>
  )
}

function ForgotPasswordPage() {
  return (
    <BackgroundWrapper>
      <div className="flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <ForgotPasswordForm />
        </div>
      </div>
    </BackgroundWrapper>
  )
}

export const Route = createFileRoute('/auth/forgot-password/')({
  beforeLoad: async () => {
    const { useAuthStore } = await import('@/stores/auth-store')
    const { isAuthenticated } = useAuthStore.getState()

    // If user is already authenticated, redirect to dashboard
    if (isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: ForgotPasswordPage,
})
